import type { Position } from '@recruitee/http-types';
import type { Visibility } from '@recruitee/visibility-types';

export type Fieldset = {
  position: number;
  title: string;
  assignedOffersCount: number;
  name: string;
  category: string;
  id: number;
  custom: boolean;
  default: boolean;
  updatedAt: string;
  fields: FieldTemplate[];
};

export type FieldTemplate = {
  id?: number | null;
  name: string;
  kind: FieldKind;
  options?: { values?: string[] };
  position?: number;
  visibility: Visibility | null;
  visible?: boolean;
};

export enum FieldKind {
  SingleLine = 'single_line',
  MultiLine = 'multi_line',
  Dropdown = 'dropdown',
  Boolean = 'boolean',
  Date = 'date',
  Number = 'number',
  Salary = 'salary',
  DateOfBirth = 'date_of_birth',
  Address = 'address',
  Education = 'education',
  Experience = 'experience',
  Gender = 'gender',
  Nationality = 'nationality',
  LanguageSkill = 'language_skill',
  Skills = 'skills',
}

export interface FieldPositionData {
  fieldsetId: number;
  fields: Position[];
}

export interface FieldsetUpdateData {
  id: number;
  name?: string;
  category?: string | null;
  default?: boolean;
}

export type FieldsetPositionData = {
  fieldsets: Position[];
};

export interface Field {
  id?: number;
  name?: string;
  fixed?: boolean;
  origin?: 'parsed' | 'manual';
  kind: FieldKind;
  values: FieldValue;
  options?: { values?: any[] };
  visibility?: Visibility | null;
  visible?: boolean;
}

export type FieldValue = Array<
  | { text: string }
  | { flag: boolean }
  | { value: string }
  | { languageCode: string; languageName?: string; level: string }
  | { countryCode: string; countryName?: string }
  | Experience
  | Education
  | { date: string }
  | { number: string }
  | { amount: string; currency: string }
>;

export const informationKinds: FieldKind[] = Object.values(FieldKind).filter(
  kind => kind !== FieldKind.Education && kind !== FieldKind.Experience,
);

export const basicKinds: FieldKind[] = [
  FieldKind.SingleLine,
  FieldKind.MultiLine,
  FieldKind.Dropdown,
  FieldKind.Boolean,
  FieldKind.Date,
];

export const specialKinds: FieldKind[] = informationKinds.filter(
  kind =>
    kind !== FieldKind.SingleLine &&
    kind !== FieldKind.MultiLine &&
    kind !== FieldKind.Dropdown &&
    kind !== FieldKind.Boolean &&
    kind !== FieldKind.Date &&
    kind !== FieldKind.Number &&
    kind !== FieldKind.Salary,
);

export type LanguageValue = Array<{ languageCode: string; languageName?: string; level: string }>;

export interface LanguageField extends Field {
  name: string;
  values: LanguageValue;
}

export type NationalityValue = Array<{ countryCode: string; countryName?: string }>;

export interface NationalityField extends Field {
  name: string;
  values: NationalityValue;
}

export interface SelectField extends Field {
  name: string;
  values: Array<{ value: string }>;
}

export interface BooleanField extends Field {
  name: string;
  values: Array<{ flag: boolean }>;
}

export interface TextField extends Field {
  name: string;
  values: Array<{ text: string }>;
}

export interface DateField extends Field {
  name: string;
  values: Array<{ date: string }>;
}

export interface NumberField extends Field {
  name: string;
  values: Array<{ number: string }>;
}

export interface SalaryField extends Field {
  name: string;
  values: Array<{ amount: string; currency: string }>;
}

export interface ExperienceField {
  id?: number;
  kind: FieldKind;
  values: Experience[];
}

export interface EducationField {
  id?: number;
  kind: FieldKind;
  values: Education[];
}

export interface Education {
  school: string;
  degree: string;
  major?: string;
  startDate: string;
  endDate?: string | null;
  description?: string;
}

export interface Experience {
  title: string;
  startDate: string;
  location: string;
  endDate?: string | null;
  description?: string;
  company: string;
}

export type SearchableField = {
  name: string;
  searchKey: string;
};
